<template>
  <transition name="modal">
    <div
      v-if="active"
      class="catalog-modal-component-container"
    >
      <div class="content">
        <button
          class="close"
          @click="hide()"
        >
          <icon
            name="cross-thin"
          />
        </button>
        <div class="addon-benefits">
          <purchase-benefit-carousel
            :name="carouselName"
            :slides="carouselSlides"
          />
        </div>
        <div class="purchase-options">
          <div class="purchase-type-toggle">
            <button
              v-for="purchaseType in purchaseTypes"
              :key="purchaseType"
              :class="['purchase-type-button', { selected: activeSection === purchaseType }]"
              @click="setActiveSection(purchaseType)"
            >
              {{ purchaseType }}
            </button>
          </div>
          <p
            v-if="activeSection === 'Extensions'"
            class="extension-disclaimer"
          >
            * Requires {{ catalogPage.title }} Add-On Package purchase.
          </p>
          <purchase-form
            v-if="activeSection !== 'Activate'"
            ref="catalog-purchase-form"
            :action="legacyLink"
            :partner-ref="partnerRef"
            :purchase-order="selectedPO"
            :sc="sc"
            :user="user"
          >
            <div class="purchase-tiles-container">
              <purchase-option-tile
                v-for="purchaseOrder in purchaseOrders"
                :key="purchaseOrder.id"
                :disabled="disablePurchaseOptions"
                :label="purchaseOrder.name"
                :price="purchaseOrder.costString"
                @tile-clicked="handleTileSelection(purchaseOrder)"
              />
            </div>
            <p class="addon-purchase-disclaimer">
              One-time payment. Access to package ends upon expiration.
            </p>
          </purchase-form>
          <div
            v-else
            class="purchase-tiles-container unrealized-purchase"
          >
            <div class="unrealized-purchase-tile">
              <p class="call-to-action">
                Is It Go Time?
              </p>
              <p class="body">
                {{ unrealizedPurchaseReceiptText }}
              </p>
              <mastery-button @mastery-button-clicked="activatePurchaseReceipt">
                ACTIVATE
              </mastery-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import Icon from '@components/singletons/icon';
import MasteryButton from '@components/ui/masteryButton';
import PurchaseBenefitCarousel from '@components/purchasing/purchase-benefit-carousel';
import PurchaseForm from '@components/singletons/purchaseForm';
import PurchaseOptionTile from '@components/purchasing/purchase-option-tile';

import CatalogPage from '@/classes/catalog-page';

import { legacyFormLink } from '@/utils';

export default {
  components: { Icon, MasteryButton, PurchaseBenefitCarousel, PurchaseForm, PurchaseOptionTile },
  data() {
    return {
      active: false,
      activeSection: 'Add-On Packages',
      catalogPage: null,
      partnerRef: undefined,
      sc: undefined,
      selectedPO: {},
    };
  },
  computed: {
    allPurchaseOrders() {
      if (this.catalogPage) {
        return this.catalogPage.catalogPagePurchaseOrders(this.$store);
      }
    },
    carouselName() {
      return `${this.catalogPage.title} Add-On Package`;
    },
    carouselSlides() {
      return this.catalogPage?.marquees || [];
    },
    disablePurchaseOptions() {
      return this.activeSection === 'Extensions' && (this.pageStatus !== CatalogPage.CATALOG_PAGE_STATUSES.ACTIVE);
    },
    legacyLink() {
      return legacyFormLink(this.$store);
    },
    listener() {
      return this.$store.getters['ui/catalogModalStatus'];
    },
    pageStatus() {
      if (this.catalogPage) {
        return this.catalogPage.catalogPageStatus(this.$store);
      }
    },
    purchaseOrders() {
      let pos = [...(this.allPurchaseOrders || [])];
      if (this.activeSection === 'Add-On Packages') {
        pos = pos.filter(po => po.purchaseOrderType === 'Add On');
      } else {
        pos = pos.filter(po => po.purchaseOrderType === 'Extension');
      }
      pos = pos.filter(po => po.showOnWeb && !po.isFree);
      return pos.sort((a, b) => a?.order - b?.order);
    },
    purchaseTypes() {
      // maps the name of the base type name to the purchaseOrdeType
      // [purchaseOrderType name, Section Title]
      let baseTypes = [['Add On', 'Add-On Packages'], ['Extension', 'Extension']];

      // don't show section unless there is a PO for it
      baseTypes = baseTypes.map(baseType => this.purchaseOrders.some(po => po.purchaseOrderType == baseType[0]) ? baseType[1] : null);

      // remove any null values in the array
      // this will show up as blank sections
      baseTypes = baseTypes.map(i => i);

      // if the users has any unrealized purchase receipts we need to as
      // special case show make sure we show that section
      if (this.unrealizedPurchaseReceipt) {
        baseTypes = ['Activate', ...baseTypes];
      }
      return baseTypes;
    },
    unrealizedPurchaseReceipt() {
      const allUnrealized = this.$store.getters['purchaseReceipts/unrealized'];
      if (allUnrealized?.length > 0 && this.catalogPage) {
        const pagePurchaseOrderIds = this.allPurchaseOrders.map(po => po.id);
        return allUnrealized.find(pr => pagePurchaseOrderIds.includes(pr.purchaseOrderId));
      }
    },
    unrealizedPurchaseReceiptText() {
      let modifier = ' ';
      if (this.unrealizedPurchaseReceipt) {
        const po = this.allPurchaseOrders.find(po => po.id === this.unrealizedPurchaseReceipt.purchaseOrderId);
        if (po?.daysUntilExpiration) {
          modifier = ` ${po.daysUntilExpiration} days worth of `;
        }
      }
      return `Hit the button to start using that${modifier}exam prep you paid for.`;
    },
    user() {
      return this.$store.getters['user/get'] || { authenticationToken: '', email: '', purchaseOrders: [] };
    },
  },
  created() {
    this.$watch('listener', (newValue, oldValue) => {
      if (newValue && !oldValue) {
        this.toggle();
      }
    });
    this.$watch('$route', (newRoute, oldRoute) => {
      if (this.active) {
        if (newRoute !== oldRoute) {
          this.hide();
        }
      }
    });
  },
  methods: {
    activatePurchaseReceipt() {
      const params = {
        source: {
          name: 'Add-On Update Screen',
          type: 'Add-On Update Screen',
        },
        po: this.$store.getters['purchaseOrders/get'][this.unrealizedPurchaseReceipt?.id],
      };
      this.$analytics.events.activatedDelayedPurchase(params);
      this.$store
        .dispatch('purchaseReceipts/activate', { id: this.unrealizedPurchaseReceipt?.id })
        .then(() => {
          this.hide();
        });
    },
    fetchPageAttachments() {
      const attachmentIds = [];
      if (this.catalogPage) {
        for (const marquee of this.catalogPage?.marquees) {
          attachmentIds.push(marquee.iconId);
          attachmentIds.push(marquee.imageId);
        }
      }
      if (attachmentIds.length > 0) {
        this.$store.dispatch('attachments/fetch', { ids: attachmentIds });
      }
    },
    handleTileSelection(po) {
      if (po) {
        this.selectedPO = po;
        const ref = this.$refs['catalog-purchase-form'].$el;
        ref.submit();
      }
    },
    hide() {
      this.active = false;
      this.$nextTick().then(() => {
        this.catalogPage = null;
        this.setActiveSection('Add-On Packages');
      });
    },
    injectCookies() {
      this.partnerRef = this.$cookies.get('partner_ref');
      this.sc = this.$cookies.get('sc');
    },
    setActiveSection(section) {
      this.activeSection = section;
    },
    toggle() {
      const state = this.$store.getters['ui/catalogModalState'];
      if (state) {
        this.setActiveSection(state);
        this.$store.dispatch('ui/clearCatalogModalState');
      }
      const catalogPageId = this.$store.getters['ui/catalogModalPageId'];
      if (catalogPageId) {
        this.catalogPage = this.$store.getters['catalog/pageList'].find(page => page.id === catalogPageId);
        this.fetchPageAttachments();
      }
      const source = this.$store.getters['ui/upsellModalSource'];
      this.$analytics.events.viewedUpgradeScreen(source);
      this.active = true;
      this.injectCookies();
      this.$store.dispatch('ui/toggleCatalogModal', { show: false, source: {} });
      this.$nextTick().then(() => {
        if (this.unrealizedPurchaseReceipt) {
          this.setActiveSection('Activate');
        }
      });
    },
  },
};
</script>

<style lang='scss' scoped>
.catalog-modal-component-container {
  background-color: rgba(0,0,0,.7);
  bottom: 0;
  display: flex;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  transition: opacity .3s ease;
  z-index: 1000;

  .content {
    background: #fafafa;
    border-radius: 20px;
    box-sizing: border-box;
    display: flex;
    margin: auto;
    max-width: rem-calc(1064);
    position: relative;
    text-align: center;
    transition: transform .3s ease;
    width: 80%;

    .close {
      align-items: center;
      background: #cccccc;
      border-radius: 100%;
      display: flex;
      height: rem-calc(28);
      justify-content: center;
      padding: 8px;
      position: absolute;
      right: rem-calc(8);
      top: rem-calc(8);
      width: rem-calc(28);
      z-index: 1;
    }

    .addon-benefits {
      box-sizing: border-box;
      padding: rem-calc(32) rem-calc(48) rem-calc(24);
      width: 35%;
    }

    .purchase-options {
      border-left: 1px solid #cccccc;
      box-sizing: border-box;
      padding: rem-calc(24) rem-calc(48);
      position: relative;
      width: calc(70% - 1px);

      .purchase-type-toggle {
        margin-bottom: rem-calc(72);

        .purchase-type-button {
          background: transparent;
          color: #767676;
          font-weight: $semi-bold;
          padding: rem-calc(4) rem-calc(4);

          &:not(:last-child) {
            margin-right: rem-calc(32);
          }

          &.selected {
            border-bottom: 2px solid #8d0098;
            color: #000000;
          }
        }
      }

      .extension-disclaimer {
        font-size: rem-calc(12);
        font-style: italic;
        left: 0;
        position: absolute;
        right: 0;
        text-align: center;
        top: rem-calc(72);
      }

      .purchase-tiles-container {
        box-sizing: border-box;
        display: flex;
        flex-direction: row;
        justify-content: space-around;
        margin-bottom: rem-calc(48);
        min-height: rem-calc(230);

        &.unrealized-purchase {
          display: block;

          .unrealized-purchase-tile {
            max-width: rem-calc(320);
            margin: 0 auto;
            background: #fff;
            border-radius: 8px;
            @include shadowed;
            padding: 2rem;

            .call-to-action {
              font-weight: $bold;
              font-size: rem-calc(26);
              margin: rem-calc(8) 0 0;
            }

            .body {
              margin: rem-calc(16) auto rem-calc(32);
              line-height: 1.5;
            }
          }
        }
      }

      .addon-purchase-disclaimer {
        color: #767676;
        font-size: rem-calc(12);
      }
    }


    @include breakpoint(medium) {
      border-radius: 0;
      flex-direction: column;
      height: 100%;
      width: 100%;

      .addon-benefits {
        padding: rem-calc(24);
        width: 100%;
      }

      .purchase-options {
        border-left: none;
        padding: 0 0 rem-calc(24);
        width: 100%;

        .purchase-type-toggle {
          background-color: #eeeeee;
          margin-bottom: rem-calc(48);

          .purchase-type-button {
            font-size: rem-calc(14);
            padding: rem-calc(12) rem-calc(4);

            &:first-child {
              margin-right: rem-calc(24);
            }
          }
        }

        .purchase-tiles-container {
          margin-bottom: rem-calc(32);
        }

        .extension-disclaimer {
          top: rem-calc(48);
        }
      }
    }
  }
}
</style>
